import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCw,
  Clock,
  CheckCircle,
  History,
  Edit,
  Calendar,
  User as UserIcon,
  AlertCircle,
  X,
  Timer,
  Settings
} from 'lucide-react';
import { SLAConfiguration } from '../types';
import apiService from '../services/api';
import { formatDateTime } from '../utils/timeUtils';

const SLAConfigurationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [configurations, setConfigurations] = useState<SLAConfiguration[]>([]);
  const [currentSLA, setCurrentSLA] = useState<SLAConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newSlaHours, setNewSlaHours] = useState(24);

  const fetchConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getSLAConfigurations();
      setConfigurations(data);

      // Get current active SLA
      try {
        const currentData = await apiService.getCurrentSLA();
        setCurrentSLA(currentData);
        if (currentData) {
          setNewSlaHours(currentData.default_sla_hours || currentData.defaultSlaHours || 24);
        }
      } catch (err) {
        const active = data.find((sla: SLAConfiguration) => sla.isActive);
        setCurrentSLA(active || null);
      }

      setError('');
    } catch (err) {
      setError(t('slaConfig.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const handleOpenDialog = () => {
    if (currentSLA) {
      setNewSlaHours(currentSLA.defaultSlaHours || 24);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError('');
  };

  const handleUpdateSLA = async () => {
    if (newSlaHours < 1 || newSlaHours > 168) {
      setError(t('slaConfig.invalidHours'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiService.updateSLA({ default_sla_hours: newSlaHours });
      setSuccess(t('slaConfig.updateSuccess'));
      handleCloseDialog();
      await fetchConfigurations();
    } catch (err: any) {
      setError(err.response?.data?.message || t('slaConfig.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-3xl mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-800/20 rounded-full -ml-36 -mb-36"></div>

          <div className="relative px-8 py-10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL ? 'إعدادات SLA' : 'SLA Configuration'}
                </h1>
                <p className="text-brand-50 text-lg font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL ? 'إدارة أوقات الاستجابة' : 'Manage response time settings'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={fetchConfigurations}
                  disabled={loading}
                  className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl text-white transition-all duration-300 hover:scale-105 disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={handleOpenDialog}
                  className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-brand-50 text-brand-700 font-bold rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <Edit className="w-5 h-5" />
                  <span>{isRTL ? 'تحديث SLA' : 'Update SLA'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-2xl p-4 flex items-start gap-3 animate-fadeIn">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 rounded-2xl p-4 flex items-start gap-3 animate-fadeIn">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800 font-medium">{success}</p>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Current SLA Stats */}
        {currentSLA && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Active SLA */}
            <div className="bg-gradient-to-br from-brand-50 to-emerald-50 rounded-3xl shadow-xl border border-brand-100 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-brand-600 rounded-2xl">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('slaConfig.currentActiveSLA')}
                  </h3>
                </div>
                <p className="text-4xl font-black text-brand-600 mb-2">
                  {currentSLA.defaultSlaHours} {t('slaConfig.hours')}
                </p>
                <p className="text-sm text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('slaConfig.effectiveFromLabel')}: {formatDateTime(currentSLA.effectiveFrom)}
                </p>
              </div>
            </div>

            {/* Time in Minutes */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl shadow-xl border border-purple-100 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-purple-600 rounded-2xl">
                    <Timer className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('slaConfig.timeInMinutes')}
                  </h3>
                </div>
                <p className="text-4xl font-black text-purple-600 mb-2">
                  {currentSLA.defaultSlaHours * 60} {t('slaConfig.minutes')}
                </p>
                <p className="text-sm text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('slaConfig.perComplaint')}
                </p>
              </div>
            </div>

            {/* Total Configurations */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl shadow-xl border border-orange-100 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-orange-600 rounded-2xl">
                    <History className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('slaConfig.totalConfigurations')}
                  </h3>
                </div>
                <p className="text-4xl font-black text-orange-600 mb-2">
                  {configurations.length}
                </p>
                <p className="text-sm text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('slaConfig.configurationsInHistory')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* No Active SLA Warning */}
        {!currentSLA && !loading && (
          <div className="mb-8 bg-orange-50 border-l-4 border-orange-500 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
              <p className="text-orange-800 font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                {t('slaConfig.noActiveSLA')}
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && configurations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-medium">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
          </div>
        ) : (
          <>
            {/* SLA History Table */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-100">
                <h2 className="text-2xl font-black text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('slaConfig.historyTitle')}
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700">
                      <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wide">
                        {t('slaConfig.configNumber')}
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wide">
                        {t('slaConfig.hoursCount')}
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wide">
                        {t('slaConfig.status')}
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wide">
                        {t('slaConfig.effectiveFrom')}
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wide">
                        {t('slaConfig.createdBy')}
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wide">
                        {t('slaConfig.createdAt')}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('slaConfig.notes')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {configurations.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <Settings className="w-10 h-10 text-gray-400" />
                            </div>
                            <p className="text-gray-500 font-medium">
                              {isRTL ? 'لا توجد إعدادات' : 'No configurations found'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      configurations.map((config, index) => (
                        <tr
                          key={config.id}
                          className="hover:bg-brand-50/30 transition-colors"
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <td className="px-6 py-4">
                            <span className="font-bold text-gray-700">#{config.id}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="p-1.5 bg-brand-100 rounded-lg">
                                <Clock className="w-4 h-4 text-brand-600" />
                              </div>
                              <span className="font-bold text-brand-600">
                                {config.defaultSlaHours} {t('slaConfig.hours')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border ${
                              config.isActive
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : 'bg-gray-100 text-gray-800 border-gray-200'
                            }`}>
                              {config.isActive ? (
                                <>
                                  <CheckCircle className="w-3 h-3" />
                                  {t('slaConfig.active')}
                                </>
                              ) : (
                                t('slaConfig.inactive')
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2 text-gray-600">
                              <Calendar className="w-4 h-4 text-brand-600" />
                              <span className="text-sm">{formatDateTime(config.effectiveFrom)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-2 text-gray-600">
                              <UserIcon className="w-4 h-4 text-brand-600" />
                              <span className="text-sm">
                                {config.createdBy
                                  ? `${config.createdBy.firstName} ${config.createdBy.lastName}`
                                  : '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2 text-gray-600">
                              <Calendar className="w-4 h-4 text-brand-600" />
                              <span className="text-sm">{formatDateTime(config.createdAt)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            <span className="text-sm line-clamp-2" dir={isRTL ? 'rtl' : 'ltr'}>
                              {config.notes || '-'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-800 font-bold mb-1" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('slaConfig.noteLabel')}:
                  </p>
                  <p className="text-blue-700" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('slaConfig.infoMessage')}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Update SLA Dialog */}
        {dialogOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full animate-scaleIn">
              <div className="sticky top-0 bg-gradient-to-r from-brand-600 to-brand-700 px-8 py-6 rounded-t-3xl z-10">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('slaConfig.updateSLATitle')}
                  </h2>
                  <button onClick={handleCloseDialog} className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Dialog Body */}
              <div className="p-8">
                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('slaConfig.hoursCount')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={newSlaHours}
                    onChange={(e) => setNewSlaHours(parseInt(e.target.value))}
                    dir="ltr"
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium text-center text-2xl"
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {t('slaConfig.hoursHelperText')}
                  </p>
                </div>

                <div className="space-y-3 bg-gradient-to-br from-brand-50 to-emerald-50 rounded-2xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>
                      {t('slaConfig.currentValue')}:
                    </span>
                    <span className="font-bold text-gray-800">
                      {currentSLA?.defaultSlaHours || 0} {t('slaConfig.hours')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>
                      {t('slaConfig.newValue')}:
                    </span>
                    <span className="font-bold text-brand-600">
                      {newSlaHours} {t('slaConfig.hours')} = {(newSlaHours / 24).toFixed(1)} {t('slaConfig.days')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200 flex gap-4">
                <button
                  onClick={handleCloseDialog}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-bold"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleUpdateSLA}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    t('slaConfig.update')
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SLAConfigurationPage;
